import React, { useState, useEffect, useRef, useMemo } from 'react';
import './RLGridWorld.css';

// Cell types
const EMPTY = 0;
const WALL = 1;  // Black
const REWARD = 2; // Gold/Yellow

// Action directions
const UP = 0;
const RIGHT = 1;
const DOWN = 2;
const LEFT = 3;

// Convergence threshold for value iteration
const CONVERGENCE_THRESHOLD = 0.0001;
const DELAY = 300; // milliseconds between iterations

const RLGridWorld = () => {
  const containerRef = useRef(null);
  const [gridSize, setGridSize] = useState({ rows: 10, cols: 15 });
  const [grid, setGrid] = useState([]);
  const [agent, setAgent] = useState({ row: 0, col: 0 });
  
  // Value iteration state
  const [valueMap, setValueMap] = useState({});
  const [policyMap, setPolicyMap] = useState({});
  const [iterationCount, setIterationCount] = useState(0);
  
  // Execution state
  const [isRunning, setIsRunning] = useState(false);
  const [phase, setPhase] = useState('idle'); // 'idle', 'optimizing', 'moving'
  const timeoutIdRef = useRef(null);
  
  // Agent tracking for stopping condition
  const previousAgentPositionsRef = useRef([]);
  const stuckThreshold = 5; // Stop after 5 iterations without movement
  
  // Check if any rewards exist in the grid
  const hasRewards = useMemo(() => {
    return grid.some(row => row.some(cell => cell === REWARD));
  }, [grid]);
  
  // Check if the grid has any non-empty cells (walls or rewards)
  const hasNonEmptyCells = useMemo(() => {
    return grid.some(row => row.some(cell => cell !== EMPTY));
  }, [grid]);
  
  // Initialize the grid based on container size
  useEffect(() => {
    const updateGridSize = () => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      const width = container.clientWidth;
      
      // Responsive grid sizing based on screen width
      let cols, rows;
      
      if (width < 350) {
        // Very small mobile screens
        cols = 8;
        rows = 6;
      } else if (width < 500) {
        // Mobile screens
        cols = 10;
        rows = 7;
      } else if (width < 700) {
        // Tablet and small screens
        cols = 12;
        rows = 8;
      } else {
        // Larger screens
        cols = 15;
        rows = 10;
      }
      
      return { rows, cols };
    };
    
    // Initialize grid
    const size = updateGridSize();
    setGridSize(size);
    initializeGrid(size);
    
    // Handle window resize
    const handleResize = () => {
      const newSize = updateGridSize();
      setGridSize(newSize);
      initializeGrid(newSize);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
    // eslint-disable-next-line
  }, []);
  
  // The main iteration loop effect
  useEffect(() => {
    // Only run this effect when isRunning is true
    if (!isRunning) return;
    
    // If just starting, make sure we're in optimizing phase
    if (phase === 'idle' && isRunning) {
      setPhase('optimizing');
      return; // This will re-trigger the effect with phase='optimizing'
    }
    
    // Function to run a single iteration
    const runOneIteration = () => {
      if (phase === 'optimizing') {
        // Do value iteration step
        const newValueMap = { ...valueMap };
        const newPolicyMap = { ...policyMap };
        const { maxDelta } = performValueIterationStep(newValueMap, newPolicyMap);
        
        // Schedule update to allow visualization
        setValueMap(newValueMap);
        setPolicyMap(newPolicyMap);
        setIterationCount(prev => prev + 1);
        
        // Check for convergence
        if (maxDelta < CONVERGENCE_THRESHOLD || iterationCount > 100) {
          // Switch to agent movement phase
          console.log("Value iteration converged, switching to agent movement");
          setPhase('moving');
        }
      } else if (phase === 'moving') {
        // Move agent according to policy
        const { done, reachedReward } = moveAgent();
        
        // Add some debugging or visual indication
        console.log("Agent moved", agent, done ? "done" : "continuing");
        
        if (done) {
          // Agent reached reward or got stuck
          setIsRunning(false);
          // Maybe show a message to the user
          setPhase(reachedReward ? 'success' : 'stuck');
        }
      }
    };
    
    // Schedule next iteration with timeout
    timeoutIdRef.current = setTimeout(runOneIteration, DELAY);
    
    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [isRunning, phase, valueMap, policyMap, iterationCount, grid, agent]); // Include all dependencies
  
  // Initialize empty grid
  const initializeGrid = (size) => {
    const newGrid = Array(size.rows).fill().map(() => Array(size.cols).fill(EMPTY));
    setGrid(newGrid);
    setAgent({ row: 0, col: 0 });
    resetValueIteration();
  };
  
  // Reset value iteration
  const resetValueIteration = () => {
    setIterationCount(0);
    setValueMap({});
    setPolicyMap({});
    previousAgentPositionsRef.current = [];
    setPhase('idle');
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };
  
  // Reset the grid
  const resetGrid = () => {
    initializeGrid(gridSize);
    setIsRunning(false);
  };
  
  // Add this new function to place the agent randomly
  const placeAgentRandomly = () => {
    // Find all empty cells
    const emptyCells = [];
    for (let r = 0; r < gridSize.rows; r++) {
      for (let c = 0; c < gridSize.cols; c++) {
        if (grid[r][c] === EMPTY) {
          emptyCells.push({ row: r, col: c });
        }
      }
    }
    
    // Select a random empty cell
    if (emptyCells.length > 0) {
      const randomIndex = Math.floor(Math.random() * emptyCells.length);
      const newPosition = emptyCells[randomIndex];
      setAgent(newPosition);
      previousAgentPositionsRef.current = []; // Reset position history
      return true;
    }
    
    return false; // No empty cells found
  };
  
  // Modify the toggleExecution function
  const toggleExecution = () => {
    if (isRunning) {
      // Stop execution
      setIsRunning(false);
    } else {
      // If we're in success or stuck state, place agent randomly and restart movement
      if (phase === 'success' || phase === 'stuck') {
        // Place agent randomly
        if (placeAgentRandomly()) {
          // Start agent in movement phase
          setPhase('moving');
          setIsRunning(true);
        }
      } else {
        // Normal start/resume execution
        if (phase === 'idle') {
          // Reset agent to top-left
          setAgent({ row: 0, col: 0 });
          previousAgentPositionsRef.current = [];
        }
        setIsRunning(true);
        setPhase(prev => prev === 'idle' ? 'optimizing' : prev);
      }
    }
  };
  
  // Handle cell click to cycle cell type
  const handleCellClick = (row, col) => {
    if (isRunning) return; // Prevent changes during execution
    
    const newGrid = [...grid];
    // Cycle cell type: EMPTY -> WALL -> REWARD -> EMPTY
    newGrid[row][col] = (newGrid[row][col] + 1) % 3;
    setGrid(newGrid);
    
    // Reset agent position if it's on a wall
    if (newGrid[agent.row][agent.col] === WALL) {
      // Find first empty cell
      let found = false;
      for (let r = 0; r < gridSize.rows && !found; r++) {
        for (let c = 0; c < gridSize.cols && !found; c++) {
          if (newGrid[r][c] === EMPTY) {
            setAgent({ row: r, col: c });
            found = true;
          }
        }
      }
    }
    
    // Reset value iteration when grid changes
    resetValueIteration();
  };
  
  // Perform a single step of value iteration
  const performValueIterationStep = (newValueMap, newPolicyMap) => {
    let maxDelta = 0;
    const gamma = 0.9; // Discount factor
    
    // For each state in the grid
    for (let r = 0; r < grid.length; r++) {
      for (let c = 0; c < grid[r].length; c++) {
        const stateKey = `${r},${c}`;
        
        // Skip walls and rewards (terminal states)
        if (grid[r][c] === WALL || grid[r][c] === REWARD) {
          continue;
        }
        
        const oldValue = valueMap[stateKey] || 0;
        let bestValue = -Infinity;
        let bestAction = -1;
        
        // For each action (UP, RIGHT, DOWN, LEFT)
        for (let action = 0; action < 4; action++) {
          // Get next position for this action 
          const [nextR, nextC] = getNextPosition(r, c, action);
          const nextStateKey = `${nextR},${nextC}`;
          const reward = grid[nextR][nextC] === REWARD ? 1 : 0;
          const nextValue = valueMap[nextStateKey] || 0;
          
          // Calculate value for this action (using Bellman equation)
          const actionValue = reward + gamma * nextValue;
          
          if (actionValue > bestValue) {
            bestValue = actionValue;
            bestAction = action;
          }
        }
        
        // Update value map and policy
        newValueMap[stateKey] = bestValue;
        newPolicyMap[stateKey] = bestAction;
        
        // Track maximum delta for convergence check
        const delta = Math.abs(bestValue - oldValue);
        maxDelta = Math.max(maxDelta, delta);
      }
    }
    
    return { maxDelta };
  };
  
  // Calculate next position based on current position and action
  const getNextPosition = (row, col, action) => {
    let nextRow = row;
    let nextCol = col;
    
    switch (action) {
      case UP:
        nextRow = Math.max(0, row - 1);
        break;
      case RIGHT:
        nextCol = Math.min(gridSize.cols - 1, col + 1);
        break;
      case DOWN:
        nextRow = Math.min(gridSize.rows - 1, row + 1);
        break;
      case LEFT:
        nextCol = Math.max(0, col - 1);
        break;
      default:
        break;
    }
    
    // Stay in place if next position is a wall
    if (grid[nextRow][nextCol] === WALL) {
      return [row, col];
    }
    
    return [nextRow, nextCol];
  };
  
  // Move agent according to policy
  const moveAgent = () => {
    const { row, col } = agent;
    const stateKey = `${row},${col}`;
    const action = policyMap[stateKey];
    
    // Get next position based on policy
    const [nextRow, nextCol] = getNextPosition(row, col, action);
    
    // Check if reached reward
    const reachedReward = grid[nextRow][nextCol] === REWARD;
    
    // Track positions to detect if agent is stuck
    const currentPos = `${nextRow},${nextCol}`;
    previousAgentPositionsRef.current.push(currentPos);
    
    // Only keep the last n positions
    if (previousAgentPositionsRef.current.length > stuckThreshold) {
      previousAgentPositionsRef.current.shift();
    }
    
    // Agent is stuck if last n positions are the same
    const isStuck = previousAgentPositionsRef.current.length === stuckThreshold && 
                    previousAgentPositionsRef.current.every(pos => pos === currentPos);
    
    // Update agent position
    setAgent({ row: nextRow, col: nextCol });
    
    // Return if we're done (reached reward or stuck)
    return { done: reachedReward || isStuck, reachedReward };
  };
  
  // Get CSS style for a cell based on its value
  const getCellStyle = (row, col) => {
    // Skip walls and rewards
    if (grid[row][col] !== EMPTY) {
      return {};
    }
    
    const stateKey = `${row},${col}`;
    const value = valueMap[stateKey] || 0;
    
    // Normalize value for color intensity (0 to 1)
    const maxValue = Object.values(valueMap).reduce((max, val) => 
      val > max && val < 100 ? val : max, 0);
    
    const normalizedValue = maxValue > 0 ? Math.max(0, value) / maxValue : 0;
    const intensity = Math.floor(normalizedValue * 255);
    return {
      backgroundColor: `rgba(0, ${intensity}, 0, ${normalizedValue * 0.3})`
    };
  };
  
  // Get policy arrow class
  const getPolicyArrow = (row, col) => {
    // Only show arrows for empty cells and after first iteration
    if (grid[row][col] !== EMPTY || iterationCount === 0) {
      return '';
    }
    
    const stateKey = `${row},${col}`;
    const policy = policyMap[stateKey];
    
    switch (policy) {
      case UP:
        return 'policy-up';
      case RIGHT:
        return 'policy-right';
      case DOWN:
        return 'policy-down';
      case LEFT:
        return 'policy-left';
      default:
        return '';
    }
  };
  
  return (
    <div className="rl-grid-container" ref={containerRef}>
      <div className="rl-grid" style={{
        gridTemplateRows: `repeat(${gridSize.rows}, 1fr)`,
        gridTemplateColumns: `repeat(${gridSize.cols}, 1fr)`
      }}>
        {grid.map((row, rowIndex) => (
          row.map((cell, colIndex) => (
            <div 
              key={`${rowIndex}-${colIndex}`}
              className={`rl-cell ${
                cell === WALL ? 'wall' : 
                cell === REWARD ? 'reward' : ''
              } ${agent.row === rowIndex && agent.col === colIndex ? 'agent' : ''} ${getPolicyArrow(rowIndex, colIndex)}`}
              onClick={() => handleCellClick(rowIndex, colIndex)}
              style={getCellStyle(rowIndex, colIndex)}
            >
            </div>
          ))
        ))}
      </div>
      
      <div className="rl-controls">
        <div className="rl-control-panel">
          <button 
            className={`rl-button ${isRunning ? 'rl-pause-button' : 
                       (phase === 'success' || phase === 'stuck') ? 'rl-restart-button' : 'rl-start-button'}`}
            onClick={toggleExecution}
            disabled={!hasRewards && phase === 'idle'}
            title={!hasRewards ? "Add at least one reward (gold) cell first" : ""}
          >
            {isRunning 
              ? `Pause ${phase === 'optimizing' ? `(Iteration #${iterationCount})` : '(Agent Moving)'}`
              : phase === 'success' 
                ? 'Place Agent Randomly & Move'
                : phase === 'stuck' 
                  ? 'Agent Stuck! Try Different Start'
                  : phase === 'moving'
                    ? 'Continue Movement'
                    : `Start ${iterationCount > 0 ? `(Iteration #${iterationCount + 1})` : ''}`
            }
          </button>
          
          <button 
            className="rl-button rl-reset-button"
            onClick={resetGrid}
            disabled={(!hasNonEmptyCells && iterationCount === 0) || isRunning}
            title={!hasNonEmptyCells ? "Add some walls or rewards first" : 
                  isRunning ? "Stop execution first" : ""}
          >
            Reset Grid
          </button>
        </div>

        <div className="rl-info">
          <div className="rl-legend">
            <div className="legend-item">
              <div className="legend-color empty"></div>
              <span>Empty</span>
            </div>
            <div className="legend-item">
              <div className="legend-color wall"></div>
              <span>Wall</span>
            </div>
            <div className="legend-item">
              <div className="legend-color reward"></div>
              <span>Reward</span>
            </div>
            <div className="legend-item">
              <div className="legend-color agent"></div>
              <span>Agent</span>
            </div>
            {iterationCount > 0 && (
              <>
                <div className="legend-item">
                  <div className="legend-color value"></div>
                  <span>Value</span>
                </div>
                <div className="legend-item">
                  <div className="legend-arrow"></div>
                  <span>Policy</span>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="rl-instructions">
          Click on cells to cycle: Empty → Wall → Reward → Empty
        </div>
      </div>
    </div>
  );
};

export default RLGridWorld; 